import {FeedFile, FeedFiles, IndexResult} from '@/api/models/FeedFiles.ts';
import CopyButton from '@/components/CopyButton.tsx';
import Date from '@/components/Date.tsx';
import NoSearchResults from '@/components/NoSearchResults.tsx';
import Pending from '@/components/Pending.tsx';
import Search from '@/components/Search.tsx';
import TitleWithDropdown from '@/components/TitleWithDropdown.tsx';
import {Toaster, ToastErrors} from '@/toaster.ts';
import {Button, HTMLTable, Icon, MenuItem, NonIdealState, Tooltip} from '@blueprintjs/core';
import {ReactElement, useCallback, useMemo, useState} from 'react';
import {Link} from 'react-router';

export default function FilesPage(): ReactElement {
	const [sequence, setSequence] = useState(0);
	const [indexing, setIndexing] = useState(false);

	const files = useMemo(FeedFiles.list, [sequence]);
	const [filter, setFilter] = useState('');

	const onReindexClick = useCallback(async () => {
		if (indexing)
			return;

		setFilter('');
		setIndexing(true);

		let result: IndexResult;

		try {
			result = await FeedFiles.index();
		} catch (error) {
			ToastErrors.fromError(error, 'Could not trigger re-index: ');
			return;
		} finally {
			setIndexing(false);
		}

		let message = 'Index complete. ';
		const changes = [];

		if (result.created)
			changes.push(`${result.created} new file${result.created !== 1 ? 's' : ''} found`);

		if (result.deleted)
			changes.push(`${result.deleted} file${result.deleted !== 1 ? 's' : ''} deleted`);

		if (result.inactive)
			changes.push(`${result.inactive} file${result.inactive !== 1 ? 's' : ''} marked as inactive`);

		if (changes.length === 0) {
			message += 'No changes found.';
		} else {
			if (changes.length === 1)
				message += changes[0];
			else {
				const end = changes.length - 1;

				message += changes.slice(0, end).join(', ');
				message += ', and ' + changes[end];
			}

			message += '.';
		}

		Toaster.success(message);

		setSequence(v => v + 1);
	}, [indexing]);

	const onRefreshClick = useCallback(() => {
		setSequence(seq => seq + 1);
	}, []);

	if (indexing) {
		return (
			<NonIdealState
				icon="data-sync"
				title="Indexing in progress..."
				description={(
					<>
						This may take a while.<br />
						Please do not navigate away from this page.
					</>
				)}
			/>
		);
	}

	return (
		<>
			<TitleWithDropdown
				title="Files"
				rightElement={<Search onSearch={setFilter} />}
			>
				<MenuItem icon="refresh" text="Refresh" onClick={onRefreshClick} />
				<MenuItem icon="database" text="Re-index files" onClick={onReindexClick} />
			</TitleWithDropdown>

			<Pending resource={files}>
				{files => <List filter={filter} refresh={onReindexClick} files={files} />}
			</Pending>
		</>
	);
}

interface ListProps {
	files: FeedFile[],
	filter: string,
	refresh: () => void,
}

function List({files, filter}: ListProps): ReactElement {
	const filtered = useMemo(() => {
		if (filter.length === 0)
			return files;

		return files.filter(v => {
			return v.name?.toLocaleLowerCase().includes(filter) ||
				v.path.toLocaleLowerCase().includes(filter) ||
				v.provider?.username.toLocaleLowerCase().includes(filter);
		});
	}, [files, filter]);

	if (files.length === 0) {
		return (
			<NonIdealState
				icon="wind"
				title="No Files Found"
				description={(
					<>
						Feed files may not have been indexed yet. Click the button<br />
						below to re-index the files list.
					</>
				)}
				action={<Button icon="database" text="Re-index Files" variant="outlined" intent="primary" />}
			/>
		);
	} else if (filtered.length === 0)
		return <NoSearchResults />;

	return (
		<HTMLTable striped={true} interactive={true} style={{width: '100%'}}>
			<thead>
				<tr>
					<th>ID.</th>
					<th>Name</th>
					<th>Provider</th>
					<th>Path</th>
					<th style={{width: 38}}>&nbsp;</th>
					<th>Last Updated</th>
					<th style={{width: 100}}>&nbsp;</th>
				</tr>
			</thead>

			<tbody>
				{filtered.map(f => (
					<tr key={f.id}>
						<td>{f.id}</td>
						<td>{f.name || '—'}</td>
						<td>{f.provider?.username ?? '—'}</td>
						<td>{f.path}</td>
						<td>
							{f.inactive && (
								<Tooltip content="This file has not been updated recently, and may be out of date.">
									<Icon intent="warning" icon="warning-sign" />
								</Tooltip>
							)}
						</td>
						<td>
							<Date date={f.lastModified} format="full" />
						</td>
						<td><Controls file={f} /></td>
					</tr>
				))}
			</tbody>
		</HTMLTable>
	);
}

interface ControlsProps {
	file: FeedFile,
}

function Controls({file}: ControlsProps): ReactElement {
	return (
		<div style={{display: 'flex', gap: 10, justifyContent: 'end'}}>
			<Tooltip placement="bottom" content="Edit">
				<Link to={`/files/${file.id}`}>
					<Button variant="minimal" icon="edit" size="small" />
				</Link>
			</Tooltip>

			<Tooltip placement="bottom" content="Download">
				<Link to={FeedFiles.getDownloadUrl(file)}>
					<Button variant="minimal" icon="download" size="small" />
				</Link>
			</Tooltip>

			<CopyButton copyText={FeedFiles.getDownloadUrl(file)} tooltip="Copy public feed URL" />
		</div>
	);
}
